import type { ITelemetryItem } from '@microsoft/applicationinsights-web'

/**
 * The basic document viewer carries its documents (names and SAS links) base64'd in the last
 * route segment. With route tracking on, that segment would land in App Insights as part of every
 * page view's uri/name/refUri, so the SAS would be readable by anyone with telemetry access.
 */
const VIEWER_SEGMENT =
  /(\/standalone\/portal-uploads\/pod\/|\/view-documents-basic\/)[^/?#\s]+/g

export const REDACTED_SEGMENT = ':id'

/** Replaces the document-carrying segment of a basic-viewer URL with `:id`. */
export function redactDocumentViewerUrl(value: string): string {
  return value.replace(VIEWER_SEGMENT, `$1${REDACTED_SEGMENT}`)
}

function redactStrings(record: Record<string, unknown> | undefined): void {
  if (!record) return
  for (const key of Object.keys(record)) {
    const value = record[key]
    if (typeof value === 'string') {
      record[key] = redactDocumentViewerUrl(value)
    }
  }
}

/**
 * App Insights telemetry initializer: scrubs basic-viewer URLs from every item, whatever its type
 * (page view, page view performance, dependency, request, exception), including the custom
 * properties and the operation name the SDK stamps from the current route. Never drops an item.
 */
export function redactDocumentViewerTelemetry(item: ITelemetryItem): boolean {
  const baseData = item.baseData as Record<string, unknown> | undefined
  redactStrings(baseData)
  redactStrings(baseData?.['properties'] as Record<string, unknown> | undefined)
  redactStrings(item.data as Record<string, unknown> | undefined)
  redactStrings(item.ext?.['trace'] as Record<string, unknown> | undefined)
  return true
}
